import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { localeDi } from '@/i18n';
import { usePreferenze, type Unita } from './preferenze';

export function convertiTemperatura(celsius: number, unita: Unita): number {
  return unita === 'F' ? celsius * 1.8 + 32 : celsius;
}

export function convertiDelta(celsius: number, unita: Unita): number {
  return unita === 'F' ? celsius * 1.8 : celsius;
}

export interface Formattatore {
  /** Temperatura con unità: "21,8 °C". `null` → "— —". */
  temp(v: number | null | undefined, decimali?: number): string;
  /** Solo il numero, per i valori grandi. */
  tempNumero(v: number | null | undefined, decimali?: number): string;
  unitaTemp: string;
  delta(v: number | null | undefined): string;
  pct(v: number | null | undefined): string;
  kw(w: number | null | undefined): string;
  numero(v: number, decimali?: number): string;
  ora(ts: number | null | undefined): string;
  data(ts: number | null | undefined): string;
  dataOra(ts: number | null | undefined): string;
  relativo(ts: number | null | undefined, adesso: number): string;
}

export const SEGNAPOSTO = '— —';

export function useFormato(): Formattatore {
  const { i18n, t } = useTranslation();
  const unita = usePreferenze((s) => s.unita);
  const locale = localeDi(i18n.language);

  const numeri = useMemo(() => new Map<number, Intl.NumberFormat>(), []);
  const numero = useCallback(
    (v: number, decimali = 1) => {
      const chiave = decimali;
      let f = numeri.get(chiave);
      if (!f) {
        f = new Intl.NumberFormat(locale, { minimumFractionDigits: decimali, maximumFractionDigits: decimali });
        numeri.set(chiave, f);
      }
      return f.format(v);
    },
    [locale, numeri],
  );

  const ora = useMemo(() => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }), [locale]);
  const data = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }), [locale]);
  const dataOra = useMemo(() => new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }), [locale]);

  return useMemo<Formattatore>(() => {
    const unitaTemp = unita === 'F' ? '°F' : '°C';
    return {
      unitaTemp,
      numero,
      temp: (v, decimali = 1) => (v === null || v === undefined ? SEGNAPOSTO : `${numero(convertiTemperatura(v, unita), decimali)} ${unitaTemp}`),
      tempNumero: (v, decimali = 1) => (v === null || v === undefined ? SEGNAPOSTO : numero(convertiTemperatura(v, unita), decimali)),
      delta: (v) => (v === null || v === undefined ? SEGNAPOSTO : `${numero(convertiDelta(v, unita), 1)} ${unitaTemp}`),
      pct: (v) => (v === null || v === undefined ? SEGNAPOSTO : `${numero(v, 0)} %`),
      kw: (w) => (w === null || w === undefined ? SEGNAPOSTO : `${numero(Math.abs(w) / 1000, Math.abs(w) >= 10_000 ? 1 : 2)} kW`),
      ora: (ts) => (ts ? ora.format(ts) : SEGNAPOSTO),
      data: (ts) => (ts ? data.format(ts) : SEGNAPOSTO),
      dataOra: (ts) => (ts ? dataOra.format(ts) : SEGNAPOSTO),
      relativo: (ts, adesso) => {
        if (!ts) return SEGNAPOSTO;
        const s = Math.max(0, Math.round((adesso - ts) / 1000));
        if (s < 60) return t('comune.adesso');
        const m = Math.round(s / 60);
        if (m < 60) return t('comune.faMinuti', { n: m });
        const h = Math.round(m / 60);
        if (h < 48) return t('comune.faOre', { n: h });
        return t('comune.faGiorni', { n: Math.round(h / 24) });
      },
    };
  }, [unita, numero, ora, data, dataOra, t]);
}
